import React, {useEffect, useMemo, useState} from 'react';
import {connect} from "react-redux";
import { Grid } from 'react-virtualized';
import 'react-virtualized/styles.css';
import {injectIntl} from 'react-intl';
import PropTypes from "prop-types";
import uniqBy from "lodash/uniqBy";

import {getCurrentLayout} from "../../../../../../../appLayout";
import {featureInterface} from "../../../../../../../../interfaces/featureInterface";
import SqftFeaturesChecker from "./SqftFeaturesChecker";
import SqftOptionsLoading from "./SqftOptionsLoading";
import SqftOptionsRelatedItemRow from "./SqftOptionsRelatedItemRow";
import SqftRelatedOptionsUnavailableMessage from "./SqftRelatedOptionsUnavailableMessage";
import {
  getFilteredRelatedFeatures,
  getRelatedFeaturesFromLayout,
} from "../../../../../../../../misc/helpers";
import {getSortedFeatures} from "../../../../../../helpers/featureHelper";
import {isEnabledColorForRenuitAndHomeDepot} from "../../../../../../helpers/sqft3dArHelper";
import {loaderDisable, loaderEnable} from "../../../../../../../ui/loaderView";
import {is3DMode} from "../../../../NavigationHelper";
import {getGridColumnDimensions, useGridDimensions} from "../../../commonTools/featuresGrid/gridDimensions";
import {getNavItems} from "../../../../../../helpers/sqftHelperUi";
import {expandSearchText} from "../../../../hooks/providers/relatedProvider";
import {
  getDevSqftRelatedParentFeature,
  loadMissingDevSqftRelatedFeatures
} from "./devSqftRelatedFeatureFallback";
import {getResolvedCabinetParentFeature} from "../helpers/cabinetParentFeatureResolver";

const SqftOptionsRelatedItemList = (props) => {
  const { windowHeight, isMobile, activeFeature, itemClickCallback, partId, shouldSortByWidth, searchInputValue,
    reCacheMenuCounter, lookViewType, selectedFilters, groheFiltersSearchText, wizardSelectedValues, intl,
    selectedFeature } = props;
  const [activeItemIndex, setActiveItemIndex] = React.useState(0);

  const [loading, setLoading] = useState(true);
  const [parentFeature, setParentFeature] = useState(null);
  const [requestedRelatedFeaturesParentId, setRequestedRelatedFeaturesParentId] = useState(null);
  const [, setRelatedFeaturesReloadCount] = useState(0);

  const {gridWidth, gridHeight} = useGridDimensions(windowHeight, isMobile);

  const layout = useMemo(() => getCurrentLayout(), []);
  const featureGroup = useMemo(() => layout.getFeatureGroupByPartId(partId), [layout, partId]);

  useEffect(() => {
    if (featureGroup) {
      setLoading(!featureGroup.allFeaturesReceived);
      if (!featureGroup.allFeaturesReceived) {
        loaderEnable();
      }
    } else {
      setLoading(false);
    }
  }, [featureGroup]);

  useEffect(() => {
    if (!featureGroup) {
      setParentFeature(null);
      return;
    }

    let parentFeature;

    const hasSelectedParentFeature = selectedFeature && selectedFeature.partId === partId;

    if (hasSelectedParentFeature) {
      parentFeature = selectedFeature;
    } else if (featureGroup.activeFeature) {
      parentFeature = featureGroup.activeFeature;
    } else if (!featureGroup.allFeaturesReceived) {
      setParentFeature(null);
      return;
    }

    if (!parentFeature && !featureGroup.activeFeature) {
      //featureGroup.setDefault(layout, true);
      parentFeature = featureGroup.activeFeature ? featureGroup.activeFeature : featureGroup.activeNotInVisualizerFeature;
    }

    if (!parentFeature) {
      setParentFeature(null);
      return;
    }

    parentFeature = getResolvedCabinetParentFeature({
      parentFeature,
      partId,
      wizardSelectedValues,
      layout,
    });

    const activeFeatureName = parentFeature.getFeatureName();
    const featuresMatchedByName = getNavItems({part: featureGroup}, activeFeatureName, {}, true, true);

    // If a wizard step was skipped, dev auto-select may choose a parent that exposes related items.
    // Do not let that fallback override a cabinet style the user selected explicitly.
    if (!hasSelectedParentFeature && featuresMatchedByName?.length === 0) {
      const allFeatures = getNavItems({part: featureGroup}, '', {}, true, true);
      if (allFeatures?.length) {
        // Dev-only fallback for auto-selected wizard state; no-op unless kvConfig.autoSelectWizardItems is enabled.
        parentFeature = getDevSqftRelatedParentFeature({
          parentFeature,
          featureGroup,
          layout,
          partId,
          selectedFilters,
          wizardSelectedValues,
        });
      }
    }



    setParentFeature(parentFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    featureGroup,
    layout,
    activeFeature,
    partId,
    loading,
    reCacheMenuCounter,
    wizardSelectedValues,
    selectedFilters,
    selectedFeature,
  ]);

  useEffect(() => {
    // Dev-only support for the auto wizard flow; regular related feature loading is unchanged.
    loadMissingDevSqftRelatedFeatures({
      layout,
      parentFeature,
      requestedRelatedFeaturesParentId,
      selectedFilters,
      setRelatedFeaturesReloadCount,
      setRequestedRelatedFeaturesParentId,
    });
  }, [parentFeature, requestedRelatedFeaturesParentId, layout, selectedFilters]);

  if (!featureGroup) {
    return <SqftOptionsLoading windowHeight={windowHeight} isMobile={isMobile} />
  }

  if (loading) {
    return (
      <SqftFeaturesChecker windowHeight={windowHeight} isMobile={isMobile} subPart={featureGroup} callBack={() => {
        if (featureGroup.allFeaturesReceived) {
          setLoading(false);
          loaderDisable();
        }
      }} />
    )
  }

  if (!parentFeature) {
    return (
      <SqftRelatedOptionsUnavailableMessage
        intl={intl}
        partId={partId}
        isMissingParentFeature={true}
      />
    );
  }

  if (is3DMode() && !isEnabledColorForRenuitAndHomeDepot(parentFeature.dbFeature.id)) {
    return (
      <SqftRelatedOptionsUnavailableMessage
        intl={intl}
        partId={partId}
        isMissingParentFeature={true}
      />
    );
  }

  const relatedFeatures = parentFeature.getFeatureRelatedFeatures();

  if (!Array.isArray(relatedFeatures) || !relatedFeatures.length) {
    return (
      <SqftRelatedOptionsUnavailableMessage
        intl={intl}
        partId={partId}
        isMissingParentFeature={false}
      />
    );
  }

  const expandedGroheFiltersSearchText = expandSearchText(groheFiltersSearchText);

  //Now let's search for the related features
  const {noFeaturesFound, relatedFeaturesFound} = getRelatedFeaturesFromLayout(
    relatedFeatures,
    parentFeature,
    expandedGroheFiltersSearchText,
    selectedFilters
  );
  if (noFeaturesFound.length) {
    console.log("ERROR: noFeaturesFound", noFeaturesFound);
  }

  let relatedItemsList = getFilteredRelatedFeatures(relatedFeaturesFound);
  relatedItemsList = uniqBy(relatedItemsList, (feature) => feature.dbFeature.id);
  let emptyUrlsLog = [];

  relatedItemsList = relatedItemsList.filter(feature => {
    if (!feature.dbFeature.thirdPartyAR3DUrls) {
      //todo we can remove this searchInputValue and search by filters
      if (searchInputValue && feature.getFeatureName().toLowerCase().includes(searchInputValue.toLowerCase())
        || !searchInputValue
      ) {
        emptyUrlsLog.push(feature.dbFeature.sharedFeatureName);
      }
    } else {
      return feature;
    }
  });

  // const relatedGroups = createRelatedGroups(relatedItemsList, [], activeQuoteIndex, intl, fabricatorData)
  // void relatedGroups
  // console.log("relatedGroups", relatedGroups);
  //
  // const groupName = parentFeature.dbFeature.groupName;
  // void groupName;

  if (emptyUrlsLog.length > 0) {
    console.log(`Features with empty thirdPartyAR3DUrls: ${emptyUrlsLog.join(', ')}`);
  }

  if (shouldSortByWidth) {
    relatedItemsList = getSortedFeatures(relatedItemsList);
  }

  if (!relatedItemsList.length) {
    return (
      <SqftRelatedOptionsUnavailableMessage
        intl={intl}
        partId={partId}
        isMissingParentFeature={false}
      />
    );
  }

  const {columnCount, rowHeight} = getGridColumnDimensions(lookViewType);

  const columnWidth = Math.floor(gridWidth / columnCount) - 8 / columnCount; // Ensure width is an integer to avoid overflow
  const rowCount = Math.ceil(relatedItemsList.length / columnCount);

  return (
    <div className="side-level2__content">
      {
        <Grid
          width={gridWidth}
          height={gridHeight}
          columnCount={columnCount}
          columnWidth={columnWidth}
          rowCount={rowCount}
          rowHeight={rowHeight}
          cellRenderer={({ columnIndex, key, rowIndex, style }) => {
            const index = rowIndex * columnCount + columnIndex;
            if (index >= relatedItemsList.length) return null;
            return (
              <SqftOptionsRelatedItemRow
                key={key}
                style={style}
                partId={partId}
                feature={relatedItemsList[index]}
                parentFeature={parentFeature}
                activeItemIndex={activeItemIndex}
                setActiveItemIndex={setActiveItemIndex}
                itemClickCallback={itemClickCallback}
              />
            )
          }}
        />
      }
    </div>
  )
};

SqftOptionsRelatedItemList.propTypes = {
  activeFeature: PropTypes.shape(featureInterface),
  partId: PropTypes.string,
  shouldSortByWidth: PropTypes.bool,
  itemClickCallback: PropTypes.func,
  //searchInputValue: PropTypes.string
};

const mapStateToProps = (store) => {
  const { menuOpened, reCacheMenuCounter } = store.sqftMenuState;
  const { visualizersLoaded } = store.serverState;
  const { windowHeight, isMobile } = store.serverState;
  const { activeQuoteIndex } = store.quotesState;
  const { sqftFromFeature } = store.sqftState;
  const { lookViewType } = store.lookState;
  const {groheFiltersSearchText} = store.groheState;
  const {selectedFilters} = store.filterState;
  const {wizardSelectedValues} = store.wizardState;
  const {selectedFeature} = store.sqftResourceState;

  return {
    menuOpened,
    windowHeight,
    isMobile,
    activeQuoteIndex,
    visualizersLoadedLength: visualizersLoaded.length,
    fabricatorData: store.fabricatorState,
    sqftFromFeature,
    reCacheMenuCounter,
    groheFiltersSearchText,
    selectedFilters,
    lookViewType,
    wizardSelectedValues,
    selectedFeature,
  };
};

const mapDispatchToProps = {
};
export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(SqftOptionsRelatedItemList)
)
